// ============================================================================
// My Work — only work (item #650, TCSD-0366 ruling 4).
//
// One list: the same rows as Home, in the same view, every band together. At
// the top, one card: yesterday on the things you follow. Tapping it opens
// yesterday's list, newest first. News never sits in the list and never moves
// the red number.
//
// The five tabs that lived here (Inbox · Command Center · Pipelines · Tasks ·
// Notifications) are gone from this page. Ben, 7 Oct 2026 (option D): the old
// Command Center and Pipelines are retired (their links land here), the
// all-buildings task list is the Tasks view behind the desktop switch below,
// and notification settings live in the account menu. The retired components
// are kept, unrouted, for one release so bringing one back is one change.
// ============================================================================
import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { useWorkScope, WorkScopeChips } from '../../shared/scope';
import { useHomeWork } from '../home/use-home-work';
import { byBand, focusElementId, focusWhenReady, type WorkRow } from '../home/home-work';
import { useDoneToday } from '../home/done-today';
import { WorkRowView, DoneTodayStrip } from '../home/work-list';
import { useRowActions } from '../home/row-actions';
import { useAuth } from '../../shared/auth';
import { newsSummary, useMyNews, useNewsPrefs } from './news';
import TaskList from '../tasks/task-list';
import { readRemembered, remember, useIsDesktop, ViewSwitch, type MyWorkView } from './view-switch';

const LEGACY_TAB: Record<string, string> = {
  command: '/my-work',
  pipelines: '/my-work',
  tasks: '/my-work?view=tasks',
  settings: '/my-work/notifications',
};

export default function MyWork() {
  const [params, setParams] = useSearchParams();
  const desktop = useIsDesktop();
  const legacy = params.get('tab');
  const asked = params.get('view');
  const view: MyWorkView =
    asked === 'tasks' || asked === 'list' ? asked : desktop ? readRemembered() : 'list';
  if (legacy && LEGACY_TAB[legacy]) return <Navigate to={LEGACY_TAB[legacy]} replace />;

  const change = (v: MyWorkView) => {
    if (desktop) remember(v);
    setParams({ view: v }, { replace: true });
  };
  const switcher = desktop || view === 'tasks' ? <ViewSwitch view={view} onChange={change} /> : null;

  if (view === 'tasks') {
    return (
      <div className="min-h-full">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 pt-4 lg:px-6">
          <span className="text-[11px] font-bold uppercase tracking-[0.07em] text-tcs-text-muted">My Work</span>
          {switcher}
        </div>
        <TaskList />
      </div>
    );
  }
  return <MyWorkList switcher={switcher} />;
}

function MyWorkList({ switcher }: { switcher: ReactNode }) {
  const navigate = useNavigate();
  const { employee } = useAuth();
  const eid = employee?.employee_id ?? null;
  const ws = useWorkScope();
  const { rows, loading, error, reload } = useHomeWork(ws);
  const { done, opened, forget, add } = useDoneToday(eid, rows, loading);
  const prefs = useNewsPrefs();
  const news = useMyNews(prefs.cadence, prefs.loaded && prefs.cadence !== 'off');
  const [toast, setToast] = useState<string | null>(null);
  const say = useCallback((m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 3200);
  }, []);
  const actions = useRowActions({ eid, add, forget, reload, say });
  const [showAll, setShowAll] = useState(false);
  const b = byBand(rows);
  const ordered = [...b.overdue, ...b.today, ...b.this_week];
  const shown = showAll ? ordered : ordered.slice(0, 15);

  useEffect(() => {
    document.title = 'My Work · TCS';
  }, []);

  const open = useCallback(
    (r: WorkRow) => {
      opened(r);
      navigate(r.link);
      focusWhenReady(focusElementId(r.focusKey));
    },
    [navigate, opened],
  );

  const windowLabel = prefs.cadence === 'weekly' ? 'This past week on the things you follow' : 'Yesterday on the things you follow';
  const summary =
    prefs.cadence === 'off'
      ? 'News is off. Turn it on in Notification settings, under your name at the top.'
      : !news.loaded
        ? 'Loading…'
        : news.items.length === 0
          ? 'Nothing happened on what you follow.'
          : newsSummary(news.items);

  return (
    <div className="min-h-full bg-tcs-bg-muted">
      <div className="mx-auto max-w-xl px-4 pb-24 pt-3">
        {switcher && <div className="mb-2.5 flex justify-end">{switcher}</div>}
        <button
          type="button"
          onClick={() => navigate('/my-work/news')}
          className="flex w-full items-center justify-between gap-2 rounded-xl border border-tcs-accent-ink/40 bg-tcs-accent-bg px-3 py-2.5 text-left"
        >
          <span className="min-w-0">
            <span className="block text-[13.5px] font-semibold text-tcs-text">{windowLabel}</span>
            <span className="block text-[12.5px] text-tcs-text-secondary">{summary}</span>
          </span>
          <ChevronRight className="h-5 w-5 shrink-0 text-tcs-accent-ink" />
        </button>

        <div className="mt-2.5">
          <WorkScopeChips scope={ws.scope} options={ws.options} onChange={ws.setScope} />
        </div>

        <div className="mb-1.5 mt-3 flex items-baseline justify-between px-0.5 text-[11px] font-bold uppercase tracking-[0.07em] text-tcs-text-muted">
          <span>My Work · {ordered.length}</span>
          <span className="font-medium normal-case tracking-normal">each clears when you fix it</span>
        </div>
        {error && <p className="mb-2 rounded-md bg-st-danger-bg px-3 py-2 text-[13px] text-st-danger-fg">{error}</p>}
        {loading && ordered.length === 0 ? (
          <p className="mt-6 text-center text-[13px] text-tcs-text-muted">Loading your work…</p>
        ) : ordered.length === 0 ? (
          <p className="mt-6 text-center text-[13px] text-tcs-text-muted">Nothing to do.</p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-tcs-border bg-tcs-bg">
            {shown.map((r) => (
              <WorkRowView key={r.key} row={r} density="comfortable" onOpen={open} onSendTo={actions.onSendTo} onClear={actions.onClear} />
            ))}
            {ordered.length > shown.length && (
              <button type="button" onClick={() => setShowAll(true)} className="block min-h-[44px] w-full border-t border-tcs-border text-[13px] text-tcs-accent-ink">
                Show all {ordered.length}
              </button>
            )}
          </div>
        )}
        <DoneTodayStrip done={done} onUndo={(d) => void actions.undo(d)} />
        <p className="mt-3 text-center text-[12px] text-tcs-text-muted">Nothing here is news. News is the card at the top.</p>
        <p className="mt-1 text-center text-[12px]">
          <Link to="/my-work/history" className="text-tcs-accent-ink underline-offset-2 hover:underline">Done &amp; archived</Link>
        </p>
      </div>
      {actions.sheets}
      {toast && (
        <div role="status" className="fixed inset-x-4 top-16 z-[60] mx-auto max-w-md rounded-xl bg-tcs-inverse px-3 py-2.5 text-[13px] text-tcs-inverse-ink shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}
